import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchEmployeeSalaries, 
  addEmployeeSalary, 
  updateEmployeeSalary, 
  deleteEmployeeSalary,
  fetchActiveEmployeeSalary,
  fetchEmployeeSalaryHistory
} from '../../redux/employeeSalarySlice';
import { 
  fetchLmsEmployees
} from '../../redux/erpSlice';
import { X, Plus, Edit, Trash2, Eye, History } from 'lucide-react';

const EmployeeSalaryManagement = () => {
  const dispatch = useDispatch();
  const { employeeSalaries, activeSalary, salaryHistory, status } = useSelector((state) => state.employeeSalaries);
  const { lmsEmployees, loading, totalCount, pageNumber, pageSize } = useSelector((state) => state.lmsEmployees);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [formData, setFormData] = useState({
    teacherEmail: '',
    employeeName: '',
    baseSalary: 0,
    allowanceAmount: 0,
    bonusAmount: 0,
    overtimeRate: 0,
    taxRate: 0,
    effectiveDate: '',
    remarks: '',
    bankName: '',
    iban: ''
  });

  useEffect(() => {
    dispatch(fetchEmployeeSalaries());
    dispatch(fetchLmsEmployees({ pageNumber: 1, pageSize: 100 }));
  }, [dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const salaryData = {
      ...formData,
      effectiveDate: new Date(formData.effectiveDate),
      isActive: true
    };

    if (isEdit) {
      dispatch(updateEmployeeSalary({ id: selectedSalary.id, updatedData: salaryData }));
    } else {
      dispatch(addEmployeeSalary(salaryData));
    }

    handleCloseModal();
  };

  const handleEdit = (salary) => {
    setSelectedSalary(salary);
    setFormData({
      teacherEmail: salary.teacherEmail,
      employeeName: salary.employeeName,
      baseSalary: salary.baseSalary,
      allowanceAmount: salary.allowanceAmount,
      bonusAmount: salary.bonusAmount,
      overtimeRate: salary.overtimeRate,
      taxRate: salary.taxRate,
      effectiveDate: salary.effectiveDate ? new Date(salary.effectiveDate).toISOString().split('T')[0] : '',
      remarks: salary.remarks || '',
      bankName: salary.bankName || '',
      iban: salary.iban || ''
    });
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this salary record?')) {
      dispatch(deleteEmployeeSalary(id));
    }
  };

  const handleViewHistory = (teacherEmail) => {
    dispatch(fetchEmployeeSalaryHistory(teacherEmail));
    setSelectedTeacher(teacherEmail);
  };

  const handleViewActive = (teacherEmail) => {
    dispatch(fetchActiveEmployeeSalary(teacherEmail));
    setSelectedTeacher(teacherEmail);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setSelectedSalary(null);
    setFormData({
      teacherEmail: '',
      employeeName: '',
      baseSalary: 0,
      allowanceAmount: 0,
      bonusAmount: 0,
      overtimeRate: 0,
      taxRate: 0,
      effectiveDate: '',
      remarks: '',
      bankName: '',
      iban: ''
    });
  };

  const handleEmployeeSelect = (employee) => {
    setFormData({
      ...formData,
      teacherEmail: employee.email,
      employeeName: `${employee.firstName} ${employee.lastName}`
    });
  };

  return (
    <div className="container mx-auto p-4 border border-[#241763]">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Employee Salary Management</h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-[#241763] text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-[#1a0f47] text-sm sm:text-base w-full sm:w-auto"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">Add Employee Salary</span>
          <span className="sm:hidden">Add Salary</span>
        </button>
      </div>

      {/* Employee Salaries Table */}
      <div className="overflow-x-auto">
        <table className="table-auto w-full mt-6">
          <thead className="py-4 border-b font-bold">
            <tr>
              <th>Employee</th>
              <th>Base Salary</th>
              <th>Allowance</th>
              <th>Bonus</th>
              <th>Overtime Rate</th>
              <th>Tax Rate</th>
              <th>Bank Name</th>
              <th>IBAN</th>
              <th>Effective Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {employeeSalaries.map((salary) => (
              <tr key={salary.id} className="p-2 py-2 hover:bg-gray-200 space-y-2">
                <td>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{salary.employeeName}</div>
                    <div className="text-xs text-gray-500">{salary.teacherEmail}</div>
                  </div>
                </td>
                <td>${salary.baseSalary?.toLocaleString()}</td>
                <td>${salary.allowanceAmount?.toLocaleString()}</td>
                <td>${salary.bonusAmount?.toLocaleString()}</td>
                <td>${salary.overtimeRate}/hr</td>
                <td>{salary.taxRate}%</td>
                <td>{salary.bankName || '-'}</td>
                <td>{salary.iban || '-'}</td>
                <td>{new Date(salary.effectiveDate).toLocaleDateString()}</td>
                <td>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    salary.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {salary.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="space-x-2 flex items-center py-2">
                  <button
                    onClick={() => handleViewActive(salary.teacherEmail)}
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    title="View Active Salary"
                  >
                    <Eye className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                  <button
                    onClick={() => handleViewHistory(salary.teacherEmail)}
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    title="View History"
                  >
                    <History className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                  <button
                    onClick={() => handleEdit(salary)}
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    title="Edit"
                  >
                    <Edit className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                  <button
                    onClick={() => handleDelete(salary.id)}
                    className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                    title="Delete"
                  >
                    <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-bold">{isEdit ? 'Edit Employee Salary' : 'Add Employee Salary'}</h2>
              <button onClick={handleCloseModal}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Employee Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Employee
                </label>
                <select
                  value={formData.teacherEmail}
                  onChange={(e) => {
                    const employee = lmsEmployees.find(emp => emp.email === e.target.value);
                    if (employee) {
                      handleEmployeeSelect(employee);
                    }
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  required
                >
                  <option value="">Select an employee</option>
                  {lmsEmployees.map((employee) => (
                    <option key={employee.id} value={employee.email}>
                      {employee.firstName} {employee.lastName} - {employee.email} ({employee.userType})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base Salary
                  </label>
                  <input
                    type="number"
                    value={formData.baseSalary}
                    onChange={(e) => setFormData({...formData, baseSalary: parseFloat(e.target.value)})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Allowance Amount
                  </label>
                  <input
                    type="number"
                    value={formData.allowanceAmount}
                    onChange={(e) => setFormData({...formData, allowanceAmount: parseFloat(e.target.value)})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bonus Amount
                  </label>
                  <input
                    type="number"
                    value={formData.bonusAmount}
                    onChange={(e) => setFormData({...formData, bonusAmount: parseFloat(e.target.value)})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Overtime Rate (per hour)
                  </label>
                  <input
                    type="number"
                    value={formData.overtimeRate}
                    onChange={(e) => setFormData({...formData, overtimeRate: parseFloat(e.target.value)})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    value={formData.taxRate}
                    onChange={(e) => setFormData({...formData, taxRate: parseFloat(e.target.value)})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Effective Date
                  </label>
                  <input
                    type="date"
                    value={formData.effectiveDate}
                    onChange={(e) => setFormData({...formData, effectiveDate: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IBAN
                  </label>
                  <input
                    type="text"
                    value={formData.iban}
                    onChange={(e) => setFormData({...formData, iban: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Remarks
                </label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  rows={3}
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#241763] text-white rounded-md hover:bg-[#1a0f47]"
                  disabled={status === 'loading'}
                >
                  {isEdit ? 'Update' : 'Add'} Salary
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Salary History Modal */}
      {selectedTeacher && salaryHistory.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-bold">Salary History for {selectedTeacher}</h2>
              <button onClick={() => setSelectedTeacher(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="table-auto w-full">
                <thead className="py-4 border-b font-bold">
                  <tr>
                    <th>Effective Date</th>
                    <th>Base Salary</th>
                    <th>Allowance</th>
                    <th>Bonus</th>
                    <th>Overtime Rate</th>
                    <th>Tax Rate</th>
                    <th>Bank Name</th>
                    <th>IBAN</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {salaryHistory.map((salary) => (
                    <tr key={salary.id} className="p-2 py-2 hover:bg-gray-200 space-y-2">
                      <td>{new Date(salary.effectiveDate).toLocaleDateString()}</td>
                      <td>${salary.baseSalary?.toLocaleString()}</td>
                      <td>${salary.allowanceAmount?.toLocaleString()}</td>
                      <td>${salary.bonusAmount?.toLocaleString()}</td>
                      <td>${salary.overtimeRate}/hr</td>
                      <td>{salary.taxRate}%</td>
                      <td>{salary.bankName || '-'}</td>
                      <td>{salary.iban || '-'}</td>
                      <td>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          salary.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {salary.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeSalaryManagement; 